import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QuickAddVendorButton from '../components/purchasing/QuickAddVendorButton';
import { CurrencySelect, UnitCostText } from '../components/CurrencySelect';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Plus, Edit2, Trash2, Search, Boxes, Filter, Mail, Download, QrCode, Printer, ExternalLink, ImageIcon, LayoutDashboard, Tag, Upload, FileSpreadsheet, ChevronDown, Layers } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import EmailPurchaserDialog from '../components/EmailPurchaserDialog';
import PrintLabelDialog from '../components/PrintLabelDialog';
import LandedCostDefaultsEditor from '../components/LandedCostDefaultsEditor';

const ItemsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [onOrder, setOnOrder] = useState({});
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [purchaserDialogOpen, setPurchaserDialogOpen] = useState(false);
  const [purchaserItem, setPurchaserItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printItem, setPrintItem] = useState(null);
  const [printItems, setPrintItems] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterSecondaryLocation, setFilterSecondaryLocation] = useState('all');
  const [riskMap, setRiskMap] = useState({});
  // iter78w — collapsible category groups (default-collapsed). Open state per
  // category persisted in localStorage. When the user types in search or
  // applies a non-"all" filter, we flatten back to a single table.
  const [groupOpenState, setGroupOpenState] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem('items:group-open') || '{}'); }
    catch { return {}; }
  });
  const toggleGroup = (cat) => {
    setGroupOpenState(prev => {
      const next = { ...prev, [cat]: !prev[cat] };
      try { window.localStorage.setItem('items:group-open', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  // iter76 — dynamic item types loaded from /item-types
  const [itemTypes, setItemTypes] = useState([]);
  // iter78h — default tax codes from /currency/settings (pre-fill new items)
  const [defaultTaxCodes, setDefaultTaxCodes] = useState({ sales: '', purchase: '' });
  // iter72 — Bulk import + QuickBooks export
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [qbExporting, setQbExporting] = useState(false);
  const [qbDialogOpen, setQbDialogOpen] = useState(false);
  const [qbAccounts, setQbAccounts] = useState({
    income_account: 'Sales of Product Income',
    expense_account: 'Cost of Goods Sold',
    inventory_asset_account: 'Inventory Asset',
  });
  // iter73 — Changes-only mode + last-export status
  const [qbChangesOnly, setQbChangesOnly] = useState(false);
  const [qbStatus, setQbStatus] = useState(null);
  // iter81 — Count Sheet upload (preview + apply)
  const [csUploadOpen, setCsUploadOpen] = useState(false);
  const [csFile, setCsFile] = useState(null);
  const [csPreview, setCsPreview] = useState(null); // dry-run result
  const [csApplied, setCsApplied] = useState(null); // final result
  const [csBusy, setCsBusy] = useState(false);
  const [csAutoCreate, setCsAutoCreate] = useState(false);
  // iter81 — Cycle-count staleness threshold (days)
  const [staleDays, setStaleDays] = useState(90);
  const [formData, setFormData] = useState({
    item_sku: '',
    item_name: '',
    description: '',
    long_description: '',
    purchase_currency: 'CAD',
    min_order_qty: '',
    order_multiple: '',
    ordering_notes: '',
    item_category: '',
    item_type: 'Component',
    base_uom: 'EA',
    track_method: 'None',
    preferred_vendor_id: '',
    vendor_sku: '',
    product_link: '',
    location_id: '',
    active_status: true,
    reorder_point: 0,
    current_quantity: 0,
    lead_time_days: 0,
    tracking_number: '',
    country_of_origin: '',
    country_of_origin_label: '',
    hs_code: '',
    eccn: '',
    // iter75 — QuickBooks-aligned pricing + tax fields
    sales_price: '',
    sales_tax_code: '',
    sales_price_includes_tax: false,
    purchase_cost: '',
    purchase_tax_code: '',
    purchase_cost_includes_tax: false,
    income_account_override: '',
    expense_account_override: '',
    inventory_asset_account_override: '',
    // iter78i — landed-cost defaults (pre-fills restock dialog)
    landed_cost_defaults: {},
  });
  const [countries, setCountries] = useState([]);

  const fetchData = async () => {
    try {
      const [itemsRes, vendorsRes, catsRes, locsRes, cooRes, typesRes, currencyRes] = await Promise.all([
        api().get('/items'),
        api().get('/vendors'),
        api().get('/item-categories?active_only=true'),
        api().get('/locations'),
        api().get('/lookups/countries-of-origin').catch(() => ({ data: { countries: [] } })),
        api().get('/item-types').catch(() => ({ data: [] })),
        api().get('/currency/settings').catch(() => ({ data: {} })),
        api().get('/items-on-order').then(r => { setOnOrder(r.data || {}); return r; }).catch(() => ({ data: {} })),
      ]);
      setItems(itemsRes.data);
      setVendors(vendorsRes.data);
      setCategories(catsRes.data || []);
      setLocations(locsRes.data || []);
      setCountries((cooRes.data && cooRes.data.countries) || []);
      setItemTypes(typesRes.data || []);
      setDefaultTaxCodes({
        sales: currencyRes.data?.default_sales_tax_code || '',
        purchase: currencyRes.data?.default_purchase_tax_code || '',
      });
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // iter81 — load cycle-count staleness threshold
  useEffect(() => {
    (async () => {
      try {
        const r = await api().get('/settings/cycle-count');
        if (r.data?.stale_days) setStaleDays(Number(r.data.stale_days));
      } catch { /* fall back to 90 */ }
    })();
  }, []);

  // iter81 — Count Sheet upload handlers
  const runCsPreview = async () => {
    if (!csFile) { toast.error('Choose an .xlsx count sheet first'); return; }
    setCsBusy(true); setCsPreview(null); setCsApplied(null);
    try {
      const form = new FormData();
      form.append('file', csFile);
      const res = await api().post('/items/count-sheet/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsPreview(res.data);
      toast.success(`Previewed ${res.data.total_rows_with_counted_qty} counted rows. Review below then click Apply Adjustments.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setCsBusy(false); }
  };

  const applyCsUpload = async () => {
    if (!csFile) return;
    setCsBusy(true);
    try {
      const form = new FormData();
      form.append('file', csFile);
      const qs = csAutoCreate ? '?auto_create_new=true' : '';
      const res = await api().post(`/items/count-sheet/reconcile${qs}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsApplied(res.data);
      toast.success(`Applied ${res.data.applied_matched_count} adjustments · created ${res.data.created_new_count} new items`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally { setCsBusy(false); }
  };

  // iter81 — staleness formatter
  const daysSince = (iso) => {
    if (!iso) return null;
    try {
      const then = new Date(iso).getTime();
      if (Number.isNaN(then)) return null;
      return Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
    } catch { return null; }
  };

  // iter40 Phase C — Health filter from URL ?filter=low_stock | out_of_stock | active
  const [filterHealth, setFilterHealth] = useState(searchParams.get('filter') || 'all');
  useEffect(() => {
    const f = searchParams.get('filter');
    if (f) setFilterHealth(f);
  }, [searchParams]);

  // Auto-open edit dialog when navigated with ?edit=:id (e.g. from ItemDetailPage)
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || items.length === 0) return;
    const target = items.find(it => it.id === editId);
    if (target) {
      handleEdit(target);
      // Clear the param so refresh doesn't re-trigger
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, searchParams]);

  const isSvc = formData.item_type === 'Service';
  const reqMissing = [
    !(formData.item_sku || '').trim() && 'SKU',
    !isSvc && (formData.purchase_cost === '' || formData.purchase_cost === null || formData.purchase_cost === undefined) && 'unit cost',
    !isSvc && !formData.preferred_vendor_id && 'preferred vendor',
  ].filter(Boolean);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem && reqMissing.length) { toast.error(`New items need ${reqMissing.join(', ')} before they can be saved`); return; }
    try {
      const payload = {
        ...formData,
        reorder_point: parseInt(formData.reorder_point) || 0,
        current_quantity: parseInt(formData.current_quantity) || 0,
        lead_time_days: parseInt(formData.lead_time_days) || 0,
        // iter75 — numeric coercion for tax/pricing fields
        sales_price: formData.sales_price === '' || formData.sales_price === null ? null : parseFloat(formData.sales_price),
        purchase_cost: formData.purchase_cost === '' || formData.purchase_cost === null ? null : parseFloat(formData.purchase_cost),
        min_order_qty: formData.min_order_qty === '' || formData.min_order_qty === null ? null : parseFloat(formData.min_order_qty),
        order_multiple: formData.order_multiple === '' || formData.order_multiple === null ? null : parseFloat(formData.order_multiple),
        preferred_vendor_id: formData.preferred_vendor_id || null,
      };
      if (editingItem) {
        await api().put(`/items/${editingItem.id}`, payload);
        toast.success('Item updated');
      } else {
        await api().post('/items', payload);
        toast.success('Item created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      item_sku: item.item_sku,
      item_name: item.item_name,
      description: item.description || '',
      long_description: item.long_description || '',
      purchase_currency: item.purchase_currency || 'CAD',
      min_order_qty: item.min_order_qty ?? '',
      order_multiple: item.order_multiple ?? '',
      ordering_notes: item.ordering_notes || '',
      item_category: item.item_category,
      item_type: item.item_type,
      base_uom: item.base_uom,
      track_method: item.track_method,
      preferred_vendor_id: item.preferred_vendor_id || '',
      vendor_sku: item.vendor_sku || '',
      product_link: item.product_link || '',
      location_id: item.location_id || '',
      secondary_location_id: item.secondary_location_id || '',
      active_status: item.active_status,
      reorder_point: item.reorder_point,
      current_quantity: item.current_quantity,
      lead_time_days: item.lead_time_days || 0,
      tracking_number: item.tracking_number || '',
      country_of_origin: item.country_of_origin || '',
      country_of_origin_label: item.country_of_origin_label || '',
      hs_code: item.hs_code || '',
      eccn: item.eccn || '',
      // iter75
      sales_price: item.sales_price ?? '',
      sales_tax_code: item.sales_tax_code || '',
      sales_price_includes_tax: !!item.sales_price_includes_tax,
      purchase_cost: item.purchase_cost ?? '',
      purchase_tax_code: item.purchase_tax_code || '',
      purchase_cost_includes_tax: !!item.purchase_cost_includes_tax,
      income_account_override: item.income_account_override || '',
      expense_account_override: item.expense_account_override || '',
      inventory_asset_account_override: item.inventory_asset_account_override || '',
      landed_cost_defaults: item.landed_cost_defaults || {},
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api().delete(`/items/${id}`);
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      item_sku: '',
      item_name: '',
      description: '',
      long_description: '',
      purchase_currency: 'CAD',
      min_order_qty: '',
      order_multiple: '',
      ordering_notes: '',
      item_category: '',
      item_type: 'Component',
      base_uom: 'EA',
      track_method: 'None',
      preferred_vendor_id: '',
      vendor_sku: '',
      product_link: '',
      location_id: '',
      secondary_location_id: '',
      active_status: true,
      reorder_point: 0,
      current_quantity: 0,
      lead_time_days: 0,
      tracking_number: '',
      country_of_origin: '',
      country_of_origin_label: '',
      hs_code: '',
      eccn: '',
      // iter75 (defaults from Admin → Settings → Currency in iter78h)
      sales_price: '',
      sales_tax_code: defaultTaxCodes.sales || '',
      sales_price_includes_tax: false,
      purchase_cost: '',
      purchase_tax_code: defaultTaxCodes.purchase || '',
      purchase_cost_includes_tax: false,
      income_account_override: '',
      expense_account_override: '',
      inventory_asset_account_override: '',
      landed_cost_defaults: {},
    });
  };

  const getLocationLabel = (locId) => {
    const l = locations.find(x => x.id === locId);
    return l ? `${l.location_name} / ${l.bin_id}` : '-';
  };

  const handleDownloadQr = async (item) => {
    try {
      const res = await api().get(`/items/${item.id}/qr`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'image/png' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.item_sku}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download QR');
    }
  };

  // iter72 — Bulk import + QuickBooks export helpers
  const downloadFile = async (path, filename, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const res = await api().get(`${path}${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadImportTemplate = async () => {
    try {
      await downloadFile('/items/bulk-import/template.csv', `item-import-template.csv`);
      toast.success('Template downloaded — open in Excel, fill rows, then upload');
    } catch { toast.error('Failed to download template'); }
  };

  const submitBulkImport = async () => {
    if (!bulkFile) { toast.error('Pick a CSV file first'); return; }
    setBulkUploading(true);
    try {
      const form = new FormData();
      form.append('file', bulkFile);
      const res = await api().post('/items/bulk-import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBulkResult(res.data);
      toast.success(`Imported ${res.data.created_count} · skipped ${res.data.skipped_count} · errors ${res.data.error_count}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setBulkUploading(false); }
  };

  const exportQuickBooks = async () => {
    setQbExporting(true);
    try {
      const params = { ...qbAccounts };
      if (qbChangesOnly) params.changes_only = 'true';
      const suffix = qbChangesOnly ? '-changes-only' : '';
      await downloadFile('/items/export/quickbooks.csv', `quickbooks-pns-export${suffix}-${new Date().toISOString().slice(0, 10)}.csv`, params);
      toast.success(qbChangesOnly ? 'Changes-only export downloaded — baseline updated' : 'QuickBooks CSV downloaded');
      setQbDialogOpen(false);
      // Refresh status so UI reflects new last-export time
      try { const r = await api().get('/items/export/quickbooks-status'); setQbStatus(r.data); } catch { /* non-fatal */ }
    } catch { toast.error('Export failed'); }
    finally { setQbExporting(false); }
  };

  // iter73 — fetch last-export status when QB dialog opens
  useEffect(() => {
    if (!qbDialogOpen) return;
    (async () => {
      try { const r = await api().get('/items/export/quickbooks-status'); setQbStatus(r.data); }
      catch { setQbStatus(null); }
    })();
  }, [qbDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const [filterCoo, setFilterCoo] = useState('all');
  const [sortField, setSortField] = useState('item_sku');

  const filteredItems = items.filter(item => {
    const matchesSearch = item.item_sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.item_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || item.item_type === filterType;
    const matchesCategory = filterCategory === 'all' || item.item_category === filterCategory;
    const matchesLocation = filterLocation === 'all' || item.location_id === filterLocation;
    const matchesSecondary = filterSecondaryLocation === 'all' || item.secondary_location_id === filterSecondaryLocation;
    const itemCoo = item.country_of_origin || 'Unknown';
    const matchesCoo = filterCoo === 'all' || itemCoo === filterCoo;
    const cq = item.current_quantity || 0;
    const rp = item.reorder_point || 0;
    let matchesHealth = true;
    if (filterHealth === 'active') matchesHealth = !!item.active_status;
    else if (filterHealth === 'out_of_stock') matchesHealth = cq <= 0;
    else if (filterHealth === 'low_stock') matchesHealth = rp > 0 && cq > 0 && cq <= rp;
    return matchesSearch && matchesFilter && matchesCategory && matchesLocation && matchesSecondary && matchesCoo && matchesHealth;
  }).sort((a, b) => {
    if (sortField === 'country_of_origin') {
      return (a.country_of_origin || 'zz').localeCompare(b.country_of_origin || 'zz');
    }
    if (sortField === 'current_quantity') {
      return (b.current_quantity || 0) - (a.current_quantity || 0);
    }
    return (a[sortField] || '').toString().localeCompare((b[sortField] || '').toString());
  });

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allFilteredSelected = filteredItems.length > 0 && filteredItems.every(it => selectedIds.has(it.id));

  // iter78w — collapsible by category. Group only when no active filtering/search.
  const hasActiveFilter = !!searchTerm
    || filterType !== 'all' || filterCategory !== 'all' || filterLocation !== 'all'
    || filterSecondaryLocation !== 'all' || filterCoo !== 'all' || filterHealth !== 'all';
  const groupedByCategory = (() => {
    const map = new Map();
    filteredItems.forEach(it => {
      const k = it.item_category || 'Uncategorized';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  })();
  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map(it => it.id)));
    }
  };
  const handleBulkPrint = () => {
    const chosen = items.filter(it => selectedIds.has(it.id));
    if (chosen.length === 0) {
      toast.error('Select at least one item');
      return;
    }
    setPrintItem(null);
    setPrintItems(chosen);
    setPrintDialogOpen(true);
  };

  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor?.vendor_name || '-';
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="items-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Item Master</h1>
          <p className="text-slate-500 text-sm">Components, materials, packaging, and consumables</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/items/dashboard')}
            className="rounded-sm"
            data-testid="open-item-dashboard-btn"
          >
            <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
          </Button>
          {/* iter72 — Bulk Import + QuickBooks Export buttons */}
          <Button
            variant="outline"
            onClick={() => { setBulkResult(null); setBulkFile(null); setBulkDialogOpen(true); }}
            className="rounded-sm border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="items-bulk-import-btn"
            title="Upload a CSV to create many items at once"
          >
            <Upload className="w-4 h-4 mr-2" /> Bulk Import
          </Button>
          <Button
            variant="outline"
            onClick={() => setQbDialogOpen(true)}
            className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="items-qb-export-btn"
            title="Download a QuickBooks Online Products & Services CSV"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> QuickBooks Export
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="items-count-sheet-btn"
                title="Download an offline physical-count Excel spreadsheet"
              >
                <Download className="w-4 h-4 mr-2" /> Count Sheet <ChevronDown className="w-3.5 h-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72" data-testid="count-sheet-menu">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Choose a format</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  try {
                    const params = {};
                    if (filterCategory && filterCategory !== 'all') params.category = filterCategory;
                    if (filterLocation && filterLocation !== 'all') params.location_id = filterLocation;
                    if (filterType && filterType !== 'all') params.item_type = filterType;
                    const qs = new URLSearchParams(params).toString();
                    const res = await api().get(`/items/export/count-sheet.xlsx${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
                    const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `physical-count-sheet-${new Date().toISOString().slice(0, 10)}.xlsx`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Count sheet downloaded — one sheet, all items.');
                  } catch { toast.error('Failed to download count sheet'); }
                }}
                data-testid="count-sheet-all-btn"
                className="cursor-pointer"
              >
                <Download className="w-4 h-4 mr-2 text-amber-600" />
                <div className="flex flex-col">
                  <span className="font-medium">All items — one sheet</span>
                  <span className="text-[11px] text-slate-500">Single tab, everything filterable.</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={async () => {
                  try {
                    const params = { by_location: 'true' };
                    if (filterCategory && filterCategory !== 'all') params.category = filterCategory;
                    if (filterType && filterType !== 'all') params.item_type = filterType;
                    // Deliberately DO NOT pass location_id — user wants one tab per location.
                    const qs = new URLSearchParams(params).toString();
                    const res = await api().get(`/items/export/count-sheet.xlsx?${qs}`, { responseType: 'blob' });
                    const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `physical-count-sheet-by-zone-${new Date().toISOString().slice(0, 10)}.xlsx`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Zone sheets downloaded — one tab per location.');
                  } catch { toast.error('Failed to download zone sheets'); }
                }}
                data-testid="count-sheet-by-zone-btn"
                className="cursor-pointer"
              >
                <Layers className="w-4 h-4 mr-2 text-blue-600" />
                <div className="flex flex-col">
                  <span className="font-medium">By location — one tab per zone</span>
                  <span className="text-[11px] text-slate-500">Each counter opens their zone&#39;s tab.</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            onClick={() => { setCsFile(null); setCsPreview(null); setCsApplied(null); setCsAutoCreate(false); setCsUploadOpen(true); }}
            className="rounded-sm border-purple-300 text-purple-700 hover:bg-purple-50"
            data-testid="items-count-sheet-upload-btn"
            title="Upload a filled Count Sheet — preview variances then apply as inventory adjustments (and optionally create new SKUs)"
          >
            <Upload className="w-4 h-4 mr-2" /> Upload Count Sheet
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                data-testid="add-item-btn"
                onClick={() => { if (!editingItem) resetForm(); }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Item' : 'Add Item'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {reqMissing.length > 0 && (
                <div className={`rounded-sm border px-3 py-2 text-xs ${editingItem ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-red-200 bg-red-50 text-red-700'}`} data-testid={editingItem ? 'item-required-warning' : 'item-required-hint'}>
                  {editingItem ? `This item is missing ${reqMissing.join(', ')}. Please fill ${reqMissing.length > 1 ? 'them' : 'it'} in so POs, costing and reorders stay accurate.` : `Required before saving: ${reqMissing.join(', ')}. $0 cost is allowed if entered on purpose.`}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SKU *</Label>
                  <Input
                    value={formData.item_sku}
                    onChange={(e) => setFormData({ ...formData, item_sku: e.target.value })}
                    placeholder="e.g., COMP-001"
                    required
                    disabled={!!editingItem}
                    className="rounded-sm mono"
                    data-testid="item-sku-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.item_name}
                    onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                    placeholder="Item name"
                    required
                    className="rounded-sm"
                    data-testid="item-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Item description..."
                  className="rounded-sm"
                  data-testid="item-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Long Description <span className="text-xs font-normal text-slate-500">(prints on POs)</span></Label>
                <Textarea value={formData.long_description || ''} onChange={(e) => setFormData({ ...formData, long_description: e.target.value })} rows={3} placeholder="Full purchasing description: material, finish, spec, drawing rev…" className="rounded-sm" data-testid="item-long-description-input" />
              </div>
              <div className="space-y-2">
                <Label>Unit Cost{isSvc ? '' : ' *'}</Label>
                <div className="flex gap-2">
                  <Input type="number" step="0.0001" min="0" value={formData.purchase_cost ?? ''} onChange={(e) => setFormData({ ...formData, purchase_cost: e.target.value })} placeholder={editingItem?.last_po_cost ? `${editingItem.last_po_cost} CAD from last PO` : 'e.g. 12.50'} className="rounded-sm mono" data-testid="item-unit-cost-input" />
                  <CurrencySelect value={formData.purchase_currency} onChange={(v) => setFormData({ ...formData, purchase_currency: v })} testId="item-currency-select" />
                </div>
                <p className="text-[10px] text-slate-400">Leave blank to use the last received PO cost{editingItem?.last_po_cost ? ` (${editingItem.last_po_cost} CAD, ${editingItem.last_po_number || 'last PO'})` : ''}. Same value as Purchase Cost under Pricing.</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>Min Order Qty</Label><Input type="number" min="0" step="any" value={formData.min_order_qty ?? ''} onChange={(e) => setFormData({ ...formData, min_order_qty: e.target.value })} className="rounded-sm mono" data-testid="item-moq-input" /></div>
                <div className="space-y-2"><Label>Order Multiple</Label><Input type="number" min="0" step="any" value={formData.order_multiple ?? ''} onChange={(e) => setFormData({ ...formData, order_multiple: e.target.value })} placeholder="e.g. 25" className="rounded-sm mono" data-testid="item-order-multiple-input" /></div>
                <div className="space-y-2 col-span-3"><Label>Ordering Notes</Label><Input value={formData.ordering_notes || ''} onChange={(e) => setFormData({ ...formData, ordering_notes: e.target.value })} placeholder="e.g. sold by the box of 100, 3-week lead on custom lengths" className="rounded-sm" data-testid="item-ordering-notes-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={formData.item_category}
                    onValueChange={(v) => {
                      if (v === '__manage__') {
                        navigate('/item-categories');
                        return;
                      }
                      setFormData({ ...formData, item_category: v });
                    }}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="item-category-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Show legacy value if it doesn't match any active category */}
                      {formData.item_category && !categories.some(c => c.name === formData.item_category) && (
                        <SelectItem value={formData.item_category}>
                          {formData.item_category} <span className="text-xs text-amber-600">(unmanaged)</span>
                        </SelectItem>
                      )}
                      {categories.map(c => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                      <SelectItem value="__manage__" className="text-orange-600 font-medium">
                        + Manage categories...
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={formData.item_type} onValueChange={(v) => setFormData({ ...formData, item_type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="item-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {itemTypes.length === 0 ? (
                        <SelectItem value="Component">Component</SelectItem>
                      ) : itemTypes.map(t => (
                        <SelectItem key={t.id} value={t.name}>{t.label || t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Base UOM *</Label>
                  <Select value={formData.base_uom} onValueChange={(v) => setFormData({ ...formData, base_uom: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="item-uom-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EA">Each (EA)</SelectItem>
                      <SelectItem value="KG">Kilogram (KG)</SelectItem>
                      <SelectItem value="LB">Pound (LB)</SelectItem>
                      <SelectItem value="M">Meter (M)</SelectItem>
                      <SelectItem value="FT">Feet (FT)</SelectItem>
                      <SelectItem value="L">Liter (L)</SelectItem>
                      <SelectItem value="GAL">Gallon (GAL)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Track Method</Label>
                  <Select value={formData.track_method} onValueChange={(v) => setFormData({ ...formData, track_method: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="item-track-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="None">None</SelectItem>
                      <SelectItem value="Lot">Lot</SelectItem>
                      <SelectItem value="Serial">Serial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Preferred Vendor{isSvc ? '' : ' *'}</Label>
                <div className="flex gap-1.5">
                <Select value={formData.preferred_vendor_id || 'none'} onValueChange={(v) => { if (v === '') return; setFormData(f => ({ ...f, preferred_vendor_id: v === 'none' ? '' : v })); }}>
                  <SelectTrigger className="rounded-sm" data-testid="item-vendor-select">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <QuickAddVendorButton api={api} onCreated={(v) => { setVendors(vs => [...vs, v].sort((a, b) => a.vendor_name.localeCompare(b.vendor_name))); setFormData(f => ({ ...f, preferred_vendor_id: v.id })); }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vendor SKU #</Label>
                  <Input
                    value={formData.vendor_sku}
                    onChange={(e) => setFormData({ ...formData, vendor_sku: e.target.value })}
                    placeholder="Vendor's part number"
                    className="rounded-sm mono"
                    data-testid="item-vendor-sku-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Primary Location</Label>
                  <Select
                    value={formData.location_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, location_id: v === 'none' ? '' : v, secondary_location_id: '' })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="item-location-select">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {locations.filter(l => l.active_status !== false && !l.parent_location_id).map(l => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.location_name} / {l.bin_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Secondary Location <span className="text-xs text-slate-400">(optional)</span></Label>
                  <Select
                    value={formData.secondary_location_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, secondary_location_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="item-secondary-location-select" disabled={!formData.location_id}>
                      <SelectValue placeholder={formData.location_id ? 'Select sub-location' : 'Pick primary first'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {locations
                        .filter(l => l.active_status !== false && l.parent_location_id === formData.location_id)
                        .map(l => (
                          <SelectItem key={l.id} value={l.id}>
                            {l.location_name} / {l.bin_id}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Lead Time (days)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={formData.lead_time_days}
                    onChange={(e) => setFormData({ ...formData, lead_time_days: e.target.value })}
                    placeholder="0"
                    className="rounded-sm mono"
                    data-testid="item-lead-time-input"
                  />
                </div>
              </div>
              {/* iter40 Phase C — Country of Origin */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Country of Origin <span className="text-xs text-slate-400">(for COO analytics)</span></Label>
                  <Select
                    value={formData.country_of_origin || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, country_of_origin: v === 'none' ? '' : v, country_of_origin_label: v === 'Other' ? formData.country_of_origin_label : '' })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="item-coo-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Unknown —</SelectItem>
                      {countries.map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.label} ({c.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.country_of_origin === 'Other' && (
                  <div className="space-y-2">
                    <Label>COO label (free-text)</Label>
                    <Input
                      value={formData.country_of_origin_label}
                      onChange={(e) => setFormData({ ...formData, country_of_origin_label: e.target.value })}
                      placeholder="e.g. Specific region"
                      className="rounded-sm"
                      data-testid="item-coo-label"
                    />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Commodity Code (HS)</Label>
                  <Input value={formData.hs_code || ''} onChange={(e) => setFormData({ ...formData, hs_code: e.target.value })} placeholder="e.g. 8481.80" className="rounded-sm mono" data-testid="item-hs-code-input" />
                </div>
                <div className="space-y-2">
                  <Label>ECCN</Label>
                  <Input value={formData.eccn || ''} onChange={(e) => setFormData({ ...formData, eccn: e.target.value })} placeholder="e.g. EAR99" className="rounded-sm mono" data-testid="item-eccn-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Lot / Tracking Number <span className="text-xs text-slate-400">(matches Batch/Lot Recall)</span></Label>
                <Input
                  value={formData.tracking_number}
                  onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
                  placeholder="e.g., LOT-2026-001 or PO-12345"
                  className="rounded-sm mono"
                  data-testid="item-tracking-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Product Link</Label>
                <Input
                  type="url"
                  value={formData.product_link}
                  onChange={(e) => setFormData({ ...formData, product_link: e.target.value })}
                  placeholder="https://vendor.example.com/product/..."
                  className="rounded-sm"
                  data-testid="item-product-link-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Current Quantity</Label>
                  <Input
                    type="number"
                    value={formData.current_quantity}
                    onChange={(e) => setFormData({ ...formData, current_quantity: e.target.value })}
                    className="rounded-sm mono"
                    data-testid="item-quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reorder Point</Label>
                  <Input
                    type="number"
                    value={formData.reorder_point}
                    onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })}
                    className="rounded-sm mono"
                    data-testid="item-reorder-point-input"
                  />
                </div>
              </div>
              {/* iter75 — QuickBooks-aligned tax + pricing fields (collapsed by default) */}
              <details className="border border-slate-200 rounded-sm p-3 bg-slate-50" data-testid="item-qb-fields-section">
                <summary className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-2">
                  <span>QuickBooks / Tax & Pricing</span>
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">populates QB export</span>
                </summary>
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Sales Price (CAD)</Label>
                      <Input
                        type="number" step="0.01"
                        value={formData.sales_price}
                        onChange={(e) => setFormData({ ...formData, sales_price: e.target.value })}
                        placeholder="e.g., 19.99"
                        className="rounded-sm mono"
                        data-testid="item-sales-price-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Sales Tax Code</Label>
                      <Input
                        value={formData.sales_tax_code}
                        onChange={(e) => setFormData({ ...formData, sales_tax_code: e.target.value })}
                        placeholder="e.g., GST 5%, HST 13%, ZERO"
                        className="rounded-sm mono"
                        data-testid="item-sales-tax-code-input"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Sales price includes tax</Label>
                    <Switch
                      checked={!!formData.sales_price_includes_tax}
                      onCheckedChange={(v) => setFormData({ ...formData, sales_price_includes_tax: v })}
                      data-testid="item-sales-price-includes-tax-switch"
                    />
                  </div>
                  <div className="border-t border-slate-200 pt-3 grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Purchase Cost (CAD)</Label>
                      <Input
                        type="number" step="0.01"
                        value={formData.purchase_cost}
                        onChange={(e) => setFormData({ ...formData, purchase_cost: e.target.value })}
                        placeholder="overrides latest cost"
                        className="rounded-sm mono"
                        data-testid="item-purchase-cost-input"
                      />
                      <p className="text-[10px] text-slate-400">Leave blank to use latest price-history entry.</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Purchase Tax Code</Label>
                      <Input
                        value={formData.purchase_tax_code}
                        onChange={(e) => setFormData({ ...formData, purchase_tax_code: e.target.value })}
                        placeholder="e.g., GST 5%, HST 13%, ZERO"
                        className="rounded-sm mono"
                        data-testid="item-purchase-tax-code-input"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Purchase cost includes tax</Label>
                    <Switch
                      checked={!!formData.purchase_cost_includes_tax}
                      onCheckedChange={(v) => setFormData({ ...formData, purchase_cost_includes_tax: v })}
                      data-testid="item-purchase-cost-includes-tax-switch"
                    />
                  </div>
                  <div className="border-t border-slate-200 pt-3 space-y-2">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">QuickBooks Account Overrides (optional)</p>
                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        value={formData.income_account_override}
                        onChange={(e) => setFormData({ ...formData, income_account_override: e.target.value })}
                        placeholder="Income Account"
                        className="rounded-sm mono text-xs"
                        data-testid="item-income-override-input"
                      />
                      <Input
                        value={formData.expense_account_override}
                        onChange={(e) => setFormData({ ...formData, expense_account_override: e.target.value })}
                        placeholder="Expense Account"
                        className="rounded-sm mono text-xs"
                        data-testid="item-expense-override-input"
                      />
                      <Input
                        value={formData.inventory_asset_account_override}
                        onChange={(e) => setFormData({ ...formData, inventory_asset_account_override: e.target.value })}
                        placeholder="Inventory Asset"
                        className="rounded-sm mono text-xs"
                        data-testid="item-inv-asset-override-input"
                      />
                    </div>
                    <p className="text-[10px] text-slate-400">Leave blank to inherit the global defaults set in the QuickBooks Export dialog.</p>
                  </div>
                </div>
              </details>

              {/* iter78i — Landed Cost Defaults */}
              <details className="rounded-sm border border-slate-200" data-testid="landed-defaults-section">
                <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Landed Cost Defaults <span className="text-xs text-slate-400 font-normal">— pre-fills restock dialog</span>
                </summary>
                <div className="p-3 space-y-2 border-t border-slate-200">
                  <LandedCostDefaultsEditor
                    value={formData.landed_cost_defaults}
                    onChange={(v) => setFormData({ ...formData, landed_cost_defaults: v })}
                  />
                </div>
              </details>

              <div className="flex items-center justify-between">
                <Label>Active Status</Label>
                <Switch
                  checked={formData.active_status}
                  onCheckedChange={(checked) => setFormData({ ...formData, active_status: checked })}
                  data-testid="item-active-switch"
                />
              </div>
              {(() => {
                const qrValue = (formData.item_sku || '').trim();
                if (!qrValue) {
                  return (
                    <div className="border border-dashed border-slate-200 rounded-sm p-3 text-xs text-slate-500" data-testid="item-qr-preview-empty">
                      Enter an SKU to generate the QR code preview.
                    </div>
                  );
                }
                return (
                  <div className="border border-slate-200 rounded-sm p-3 flex items-center gap-3" data-testid="item-qr-preview">
                    <div className="bg-white border border-slate-100 p-1">
                      <QRCodeSVG value={qrValue} size={80} level="M" includeMargin={false} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-700 flex items-center gap-1">
                        <QrCode className="w-3 h-3" /> QR Code {editingItem ? '' : '(preview)'}
                      </div>
                      <div className="text-sm font-medium text-slate-800 truncate" data-testid="item-qr-preview-name">
                        {formData.item_name || <span className="text-slate-400">Item name</span>}
                      </div>
                      <div className="text-xs text-slate-500 mono break-all">{qrValue}</div>
                      {!editingItem && (
                        <div className="text-xs text-slate-400 mt-1">
                          QR will encode the item's detail-page URL once saved.
                        </div>
                      )}
                    </div>
                    {editingItem && (
                      <div className="flex flex-col gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadQr(editingItem)}
                          className="rounded-sm"
                          data-testid="item-qr-download-btn"
                        >
                          <Download className="w-3 h-3 mr-1" /> Download
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => { setPrintItem(editingItem); setPrintDialogOpen(true); }}
                          className="rounded-sm"
                          data-testid="item-qr-print-btn"
                        >
                          <Printer className="w-3 h-3 mr-1" /> Print
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="item-submit-btn">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by SKU or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 rounded-sm"
                data-testid="item-search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[160px] rounded-sm" data-testid="item-filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {itemTypes.length === 0 ? (
                  <>
                    <SelectItem value="RawMaterial">Raw Material</SelectItem>
                    <SelectItem value="Component">Component</SelectItem>
                    <SelectItem value="Packaging">Packaging</SelectItem>
                    <SelectItem value="Consumable">Consumable</SelectItem>
                  </>
                ) : itemTypes.map(t => (
                  <SelectItem key={t.id} value={t.name} data-testid={`type-filter-option-${t.name}`}>
                    {t.label || t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px] rounded-sm" data-testid="item-filter-category-select">
                <Tag className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.name} data-testid={`category-filter-option-${c.name}`}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterHealth !== 'all' && (
              <Badge className="bg-orange-500 cursor-pointer flex items-center gap-1" onClick={() => { setFilterHealth('all'); searchParams.delete('filter'); setSearchParams(searchParams, { replace: true }); }} data-testid="health-filter-clear">
                Health: {filterHealth.replace('_', ' ')} ✕
              </Badge>
            )}
            <Select value={filterCoo} onValueChange={setFilterCoo}>              <SelectTrigger className="w-[170px] rounded-sm" data-testid="item-filter-coo-select">
                <SelectValue placeholder="Country of Origin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                <SelectItem value="Unknown">— Unknown —</SelectItem>
                {countries.map(c => (
                  <SelectItem key={c.code} value={c.code} data-testid={`coo-filter-${c.code}`}>{c.label} ({c.code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortField} onValueChange={setSortField}>
              <SelectTrigger className="w-[140px] rounded-sm" data-testid="item-sort-select">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="item_sku">Sort: SKU</SelectItem>
                <SelectItem value="item_name">Sort: Name</SelectItem>
                <SelectItem value="country_of_origin">Sort: Country</SelectItem>
                <SelectItem value="current_quantity">Sort: Qty (desc)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterLocation} onValueChange={(v) => { setFilterLocation(v); setFilterSecondaryLocation('all'); }}>
              <SelectTrigger className="w-[180px] rounded-sm" data-testid="item-filter-location-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.filter(l => !l.parent_location_id).map(l => (
                  <SelectItem key={l.id} value={l.id} data-testid={`location-filter-option-${l.id}`}>
                    {l.location_name} / {l.bin_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterLocation !== 'all' && locations.some(l => l.parent_location_id === filterLocation) && (
              <Select value={filterSecondaryLocation} onValueChange={setFilterSecondaryLocation}>
                <SelectTrigger className="w-[180px] rounded-sm" data-testid="item-filter-secondary-location-select">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sub-locations</SelectItem>
                  {locations.filter(l => l.parent_location_id === filterLocation).map(l => (
                    <SelectItem key={l.id} value={l.id} data-testid={`secondary-location-filter-option-${l.id}`}>
                      {l.location_name} / {l.bin_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {selectedIds.size > 0 && (
              <Button
                onClick={handleBulkPrint}
                className="bg-orange-600 hover:bg-orange-700 rounded-sm ml-auto"
                data-testid="bulk-print-btn"
              >
                <Printer className="w-4 h-4 mr-2" /> Print {selectedIds.size} label{selectedIds.size === 1 ? '' : 's'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="spinner" />
            </div>
          ) : filteredItems.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={toggleSelectAll}
                        data-testid="select-all-items"
                        className="cursor-pointer"
                      />
                    </TableHead>
                    <TableHead className="w-14">Photo</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead className="text-right">Unit Cost</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>COO</TableHead>
                    <TableHead>Link</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // iter78w — render a single item row (extracted so we can reuse it inside category groups)
                    const renderRow = (item) => (
                    <TableRow key={item.id} data-testid={`item-row-${item.id}`}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={() => toggleSelected(item.id)}
                          data-testid={`select-item-${item.id}`}
                          className="cursor-pointer"
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/items/view/${item.id}`)}
                          className="block w-10 h-10 bg-slate-50 border border-slate-200 rounded-sm overflow-hidden hover:border-orange-400 transition"
                          title="View item details"
                          data-testid={`item-photo-${item.id}`}
                        >
                          {item.image_data ? (
                            <img src={item.image_data} alt={item.item_name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="flex items-center justify-center w-full h-full text-slate-300">
                              <ImageIcon className="w-4 h-4" />
                            </div>
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="mono font-medium">
                        <button
                          onClick={() => navigate(`/items/view/${item.id}`)}
                          className="text-slate-800 hover:text-orange-600 hover:underline"
                          data-testid={`item-sku-link-${item.id}`}
                        >
                          {item.item_sku}
                        </button>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/items/view/${item.id}`)}
                          className="text-slate-800 hover:text-orange-600 hover:underline text-left"
                          data-testid={`item-name-link-${item.id}`}
                        >
                          {item.item_name}
                        </button>
                      </TableCell>
                      <TableCell>
                        <span className="status-badge status-draft">{item.item_type}</span>
                      </TableCell>
                      <TableCell className="text-slate-500">{item.item_category}</TableCell>
                      <TableCell className="mono">
                        <span className={item.current_quantity < item.reorder_point ? 'text-red-600 font-semibold' : ''}>
                          {item.current_quantity} {item.base_uom}
                        </span>
                        {onOrder[item.id] && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-800 text-[10px] font-semibold border border-blue-200" title={`On order: ${onOrder[item.id].po_numbers.join(', ')}${onOrder[item.id].next_expected ? ` · next expected ${onOrder[item.id].next_expected}` : ''}`} data-testid={`item-on-order-${item.id}`}>
                            +{onOrder[item.id].on_order} on order → {Number(item.current_quantity || 0) + onOrder[item.id].on_order}{onOrder[item.id].next_expected ? ` · ${onOrder[item.id].next_expected}` : ''}
                          </span>
                        )}
                        {(() => {
                          // iter81 — cycle-count staleness badge
                          const since = daysSince(item.last_counted_at);
                          if (since === null) {
                            return (
                              <span
                                className="ml-2 inline-block px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 text-[10px] font-semibold border border-slate-200"
                                title="This item has never been counted via a physical inventory sheet."
                                data-testid={`item-stale-never-${item.id}`}
                              >NEVER COUNTED</span>
                            );
                          }
                          if (since >= staleDays) {
                            return (
                              <span
                                className="ml-2 inline-block px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 text-[10px] font-semibold border border-amber-200"
                                title={`Last counted ${since} days ago (threshold ${staleDays}). Time for a fresh cycle count.`}
                                data-testid={`item-stale-${item.id}`}
                              >{since}d STALE</span>
                            );
                          }
                          return null;
                        })()}
                        {item.current_quantity < item.reorder_point && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-2 text-orange-600"
                            onClick={() => { setPurchaserItem(item); setPurchaserDialogOpen(true); }}
                            title="Email purchaser to reorder"
                            data-testid={`email-purchaser-low-stock-${item.id}`}
                          >
                            <Mail className="w-3 h-3" />
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm"><UnitCostText item={item} testId={`item-unit-cost-${item.id}`} /></TableCell>
                      <TableCell>
                        {item.preferred_vendor_id ? (
                          <button
                            onClick={() => navigate('/vendors')}
                            className="text-slate-600 hover:text-orange-600 hover:underline cursor-pointer"
                          >
                            {getVendorName(item.preferred_vendor_id)}
                          </button>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                        {item.vendor_sku && (
                          <div className="text-xs text-slate-400 mono">SKU: {item.vendor_sku}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600" data-testid={`item-location-${item.id}`}>
                        {item.location_id ? (
                          <div className="flex flex-col">
                            <span>{getLocationLabel(item.location_id)}</span>
                            {item.secondary_location_id && (
                              <span className="text-xs text-slate-400" data-testid={`item-secondary-location-${item.id}`}>
                                ↳ {getLocationLabel(item.secondary_location_id)}
                              </span>
                            )}
                          </div>
                        ) : <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell className="text-xs mono text-slate-600" data-testid={`item-coo-${item.id}`}>
                        {item.country_of_origin ? (
                          <Badge variant="outline" className="font-mono text-[10px]">{item.country_of_origin}</Badge>
                        ) : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell>
                        {item.product_link ? (
                          <a
                            href={item.product_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 hover:underline inline-flex items-center gap-1 text-sm"
                            data-testid={`open-product-link-${item.id}`}
                          >
                            Visit <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge ${item.active_status ? 'status-released' : 'status-obsolete'}`}>
                          {item.active_status ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadQr(item)}
                            title="Download QR code"
                            data-testid={`download-qr-${item.id}`}
                          >
                            <QrCode className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setPrintItems(null); setPrintItem(item); setPrintDialogOpen(true); }}
                            title="Print label"
                            data-testid={`print-label-${item.id}`}
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-item-${item.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                    if (hasActiveFilter) {
                      return filteredItems.map(renderRow);
                    }
                    // Grouped (default) — each category collapsed by default; click header to expand.
                    return groupedByCategory.flatMap(([cat, rows]) => {
                      const isOpen = !!groupOpenState[cat];
                      const headerRow = (
                        <TableRow key={`grp-h-${cat}`} className="bg-slate-100 hover:bg-slate-200 cursor-pointer" onClick={() => toggleGroup(cat)} data-testid={`category-group-${cat}`}>
                          <TableCell colSpan={12} className="py-2 font-semibold text-slate-700 select-none">
                            <span className="inline-block w-4 text-orange-600">{isOpen ? '▼' : '▶'}</span>
                            <span className="font-mono text-xs uppercase tracking-wider mr-2 text-slate-500">{cat}</span>
                            <span className="text-xs text-slate-500">· {rows.length} {rows.length === 1 ? 'item' : 'items'}</span>
                          </TableCell>
                        </TableRow>
                      );
                      return isOpen ? [headerRow, ...rows.map(renderRow)] : [headerRow];
                    });
                  })()}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <Boxes className="w-12 h-12 mb-3 text-slate-300" />
              <p>No items found</p>
            </div>
          )}
        </CardContent>
      </Card>

      <EmailPurchaserDialog
        open={purchaserDialogOpen}
        onOpenChange={setPurchaserDialogOpen}
        requestType="low_stock"
        item={purchaserItem}
      />

      <PrintLabelDialog
        open={printDialogOpen}
        onOpenChange={(open) => {
          setPrintDialogOpen(open);
          if (!open) { setPrintItems(null); setPrintItem(null); }
        }}
        item={printItem}
        items={printItems}
      />

      {/* iter72 — Bulk Import dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(o) => { setBulkDialogOpen(o); if (!o) { setBulkFile(null); setBulkResult(null); } }}>
        <DialogContent className="sm:max-w-2xl" data-testid="bulk-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-blue-600" /> Bulk Import Items</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-900">
              <p className="font-semibold mb-1">How it works</p>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Download the template — required columns are <code>item_sku</code>, <code>item_name</code>, <code>item_category</code>, <code>base_uom</code>.</li>
                <li>Fill in your products in Excel (one row per item). Save as CSV.</li>
                <li>Upload the file here. Existing SKUs are <strong>skipped</strong> — new SKUs are created.</li>
              </ol>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Button variant="outline" onClick={downloadImportTemplate} data-testid="download-template-btn"><Download className="w-4 h-4 mr-1" /> Download Template (CSV)</Button>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkResult(null); }}
                  className="text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                  data-testid="bulk-import-file-input"
                />
              </div>
            </div>
            {bulkFile && !bulkResult && (
              <div className="text-xs text-slate-600 mono">Selected: {bulkFile.name} ({(bulkFile.size / 1024).toFixed(1)} KB)</div>
            )}
            {bulkResult && (
              <div className="border border-slate-200 rounded-sm p-3 text-xs space-y-2" data-testid="bulk-import-result">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                    <div className="text-2xl font-bold text-emerald-700">{bulkResult.created_count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Created</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded p-2">
                    <div className="text-2xl font-bold text-amber-700">{bulkResult.skipped_count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-700">Skipped (dup SKU)</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded p-2">
                    <div className="text-2xl font-bold text-red-700">{bulkResult.error_count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-red-700">Errors</div>
                  </div>
                </div>
                {bulkResult.errors?.length > 0 && (
                  <div>
                    <div className="font-semibold text-red-700 mb-1">Errors:</div>
                    <ul className="space-y-0.5 ml-3 text-red-700">
                      {bulkResult.errors.slice(0, 10).map((e, i) => <li key={i} className="list-disc">Row {e.row}: {e.reason}{e.item_sku ? ` (SKU ${e.item_sku})` : ''}</li>)}
                      {bulkResult.errors.length > 10 && <li className="italic text-slate-500">+{bulkResult.errors.length - 10} more</li>}
                    </ul>
                  </div>
                )}
                {bulkResult.skipped?.length > 0 && (
                  <div>
                    <div className="font-semibold text-amber-700 mb-1">Skipped duplicates:</div>
                    <ul className="space-y-0.5 ml-3 text-amber-700 mono">
                      {bulkResult.skipped.slice(0, 5).map((s, i) => <li key={i} className="list-disc">{s.item_sku}</li>)}
                      {bulkResult.skipped.length > 5 && <li className="italic text-slate-500">+{bulkResult.skipped.length - 5} more</li>}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>{bulkResult ? 'Close' : 'Cancel'}</Button>
              <Button onClick={submitBulkImport} disabled={!bulkFile || bulkUploading} className="bg-slate-900 hover:bg-slate-800" data-testid="bulk-import-submit-btn">
                <Upload className="w-4 h-4 mr-1" /> {bulkUploading ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter72 — QuickBooks Export dialog */}
      <Dialog open={qbDialogOpen} onOpenChange={setQbDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="qb-export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-600" /> Export to QuickBooks Online</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs text-emerald-900">
              Generates a CSV in QuickBooks Online's <strong>Products & Services</strong> import format. Sales price/rate is left blank for your accountant to fill in. Inactive items are skipped.
            </div>
            {/* iter73 — Changes-only toggle + last-export badge */}
            <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="qb-changes-only-label">
                <input
                  type="checkbox"
                  checked={qbChangesOnly}
                  onChange={(e) => setQbChangesOnly(e.target.checked)}
                  className="mt-0.5 accent-emerald-600"
                  data-testid="qb-changes-only-checkbox"
                />
                <span className="text-sm">
                  <span className="font-medium">Changes since last export only</span>
                  <span className="block text-[11px] text-slate-500">Emits only items whose quantity or unit cost has changed, plus any new SKUs. The baseline is updated at the end of the run so weekly accountant exports stay small.</span>
                </span>
              </label>
              {qbStatus?.exported_at && (
                <div className="text-[11px] text-slate-500 ml-6 mono" data-testid="qb-last-exported">
                  Last changes-only export: {new Date(qbStatus.exported_at).toLocaleString()}
                  {qbStatus.exported_by_name && <span> · by {qbStatus.exported_by_name}</span>}
                  {typeof qbStatus.emitted_count === 'number' && <span> · {qbStatus.emitted_count} rows</span>}
                </div>
              )}
              {!qbStatus?.exported_at && qbChangesOnly && (
                <div className="text-[11px] text-amber-700 ml-6" data-testid="qb-first-changes-export">
                  No baseline yet — this first run will emit <strong>every active item</strong> and become the baseline.
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Income account</Label>
                <Input value={qbAccounts.income_account} onChange={(e) => setQbAccounts(s => ({ ...s, income_account: e.target.value }))} className="rounded-sm mono text-sm" data-testid="qb-income-input" />
              </div>
              <div>
                <Label className="text-xs">Expense account (COGS)</Label>
                <Input value={qbAccounts.expense_account} onChange={(e) => setQbAccounts(s => ({ ...s, expense_account: e.target.value }))} className="rounded-sm mono text-sm" data-testid="qb-expense-input" />
              </div>
              <div>
                <Label className="text-xs">Inventory asset account</Label>
                <Input value={qbAccounts.inventory_asset_account} onChange={(e) => setQbAccounts(s => ({ ...s, inventory_asset_account: e.target.value }))} className="rounded-sm mono text-sm" data-testid="qb-asset-input" />
              </div>
              <p className="text-[11px] text-slate-500">These names must match accounts that already exist in your QuickBooks Chart of Accounts. The defaults work for most standard QBO setups.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setQbDialogOpen(false)}>Cancel</Button>
              <Button onClick={exportQuickBooks} disabled={qbExporting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="qb-export-submit-btn">
                <Download className="w-4 h-4 mr-1" /> {qbExporting ? 'Exporting…' : 'Download CSV'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter81 — Upload Count Sheet dialog (preview → apply) */}
      <Dialog open={csUploadOpen} onOpenChange={(o) => { setCsUploadOpen(o); if (!o) { setCsFile(null); setCsPreview(null); setCsApplied(null); } }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="count-sheet-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-purple-600" /> Upload Filled Count Sheet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!csApplied && (
              <div className="bg-purple-50 border border-purple-200 rounded-sm p-3 text-xs text-purple-900">
                <p className="font-semibold mb-1">Two-step process (safe)</p>
                <ol className="list-decimal ml-4 space-y-0.5">
                  <li>Upload your filled <strong>.xlsx</strong> from the field. Rows with a blank Counted Qty are skipped.</li>
                  <li>Review the Preview panel below — matched items, new SKUs, and any errors.</li>
                  <li>Click <strong>Apply Adjustments</strong>. Only then are transactions written and item quantities updated.</li>
                </ol>
              </div>
            )}
            {!csPreview && !csApplied && (
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => { setCsFile(e.target.files?.[0] || null); setCsPreview(null); setCsApplied(null); }}
                  className="text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                  data-testid="count-sheet-file-input"
                />
                {csFile && <span className="text-xs mono text-slate-600">{csFile.name} ({(csFile.size / 1024).toFixed(1)} KB)</span>}
                <Button onClick={runCsPreview} disabled={!csFile || csBusy} className="ml-auto bg-purple-600 hover:bg-purple-700" data-testid="count-sheet-preview-btn">
                  {csBusy ? 'Previewing…' : 'Preview'}
                </Button>
              </div>
            )}

            {csPreview && !csApplied && (
              <div className="space-y-3" data-testid="count-sheet-preview-panel">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-slate-50 border border-slate-200 rounded p-2">
                    <div className="text-2xl font-bold text-slate-700">{csPreview.total_rows_with_counted_qty}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-600">Counted Rows</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                    <div className="text-2xl font-bold text-emerald-700">{csPreview.matched_count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Matched</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded p-2">
                    <div className="text-2xl font-bold text-blue-700">{csPreview.new_count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-blue-700">New SKUs</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded p-2">
                    <div className="text-2xl font-bold text-red-700">{csPreview.error_count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-red-700">Errors</div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                  <div className="border border-emerald-100 rounded p-1.5"><span className="text-emerald-700 font-semibold">↑ {csPreview.variance_summary?.increases || 0}</span> increases</div>
                  <div className="border border-red-100 rounded p-1.5"><span className="text-red-700 font-semibold">↓ {csPreview.variance_summary?.decreases || 0}</span> decreases</div>
                  <div className="border border-slate-100 rounded p-1.5"><span className="text-slate-700 font-semibold">= {csPreview.variance_summary?.no_change || 0}</span> unchanged</div>
                  <div className="border border-purple-100 rounded p-1.5">Net Δ: <span className="text-purple-700 font-semibold">{csPreview.variance_summary?.net_units || 0}</span></div>
                </div>

                {csPreview.matched?.length > 0 && (
                  <details open className="border border-slate-200 rounded-sm">
                    <summary className="p-2 bg-slate-50 text-xs font-semibold cursor-pointer">Matched adjustments ({csPreview.matched.length})</summary>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-slate-100 sticky top-0"><tr>
                          <th className="p-1.5 text-left">SKU</th><th className="p-1.5 text-left">Name</th>
                          <th className="p-1.5 text-right">Expected</th><th className="p-1.5 text-right">Counted</th><th className="p-1.5 text-right">Variance</th>
                        </tr></thead>
                        <tbody>
                          {csPreview.matched.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100" data-testid={`cs-matched-row-${r.matched_item_sku}`}>
                              <td className="p-1.5 mono">{r.matched_item_sku}</td>
                              <td className="p-1.5">{r.matched_item_name}</td>
                              <td className="p-1.5 text-right">{r.db_current_quantity}</td>
                              <td className="p-1.5 text-right">{r.counted_qty}</td>
                              <td className={`p-1.5 text-right font-semibold ${r.variance > 0 ? 'text-emerald-700' : r.variance < 0 ? 'text-red-700' : 'text-slate-500'}`}>
                                {r.variance > 0 ? '+' : ''}{r.variance}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}

                {csPreview.new_rows?.length > 0 && (
                  <details className="border border-blue-200 rounded-sm">
                    <summary className="p-2 bg-blue-50 text-xs font-semibold cursor-pointer text-blue-900">New SKUs ({csPreview.new_rows.length}) — not in the database yet</summary>
                    <div className="p-2">
                      <label className="flex items-center gap-2 text-xs mb-2">
                        <input type="checkbox" checked={csAutoCreate} onChange={(e) => setCsAutoCreate(e.target.checked)} data-testid="cs-auto-create-checkbox" />
                        <span>Auto-create these new SKUs when I apply (uses Counted Qty as starting quantity)</span>
                      </label>
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-[11px]">
                          <thead className="bg-slate-100"><tr>
                            <th className="p-1.5 text-left">SKU</th><th className="p-1.5 text-left">Name</th>
                            <th className="p-1.5 text-left">Category</th><th className="p-1.5 text-left">Type</th>
                            <th className="p-1.5 text-right">Counted</th>
                          </tr></thead>
                          <tbody>
                            {csPreview.new_rows.map((r, i) => (
                              <tr key={i} className="border-t border-slate-100">
                                <td className="p-1.5 mono">{r.item_sku}</td>
                                <td className="p-1.5">{r.item_name}</td>
                                <td className="p-1.5">{r.item_category}</td>
                                <td className="p-1.5">{r.item_type}</td>
                                <td className="p-1.5 text-right">{r.counted_qty}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </details>
                )}

                {csPreview.errors?.length > 0 && (
                  <details className="border border-red-200 rounded-sm">
                    <summary className="p-2 bg-red-50 text-xs font-semibold cursor-pointer text-red-900">Errors ({csPreview.errors.length})</summary>
                    <ul className="p-2 space-y-0.5 text-[11px] text-red-800">
                      {csPreview.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row_num}: {e.reason}{e.item_sku ? ` (SKU ${e.item_sku})` : ''}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}

            {csApplied && (
              <div className="space-y-3" data-testid="count-sheet-applied-panel">
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs text-emerald-900">
                  <p className="font-semibold">✓ Applied</p>
                  <p>{csApplied.applied_matched_count} adjustment transactions written · {csApplied.created_new_count} new items created · {csApplied.skipped_new_count} new rows skipped · {csApplied.error_count} errors.</p>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                    <div className="text-2xl font-bold text-emerald-700">{csApplied.applied_matched_count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Adjusted</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded p-2">
                    <div className="text-2xl font-bold text-blue-700">{csApplied.created_new_count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-blue-700">Created</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded p-2">
                    <div className="text-2xl font-bold text-amber-700">{csApplied.skipped_new_count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-700">Skipped</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded p-2">
                    <div className="text-2xl font-bold text-red-700">{csApplied.error_count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-red-700">Errors</div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={() => setCsUploadOpen(false)}>{csApplied ? 'Close' : 'Cancel'}</Button>
              {csPreview && !csApplied && (
                <Button
                  onClick={applyCsUpload}
                  disabled={csBusy || (csPreview.matched_count === 0 && !(csAutoCreate && csPreview.new_count > 0))}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="count-sheet-apply-btn"
                >
                  {csBusy ? 'Applying…' : `Apply Adjustments${csAutoCreate && csPreview.new_count > 0 ? ` + Create ${csPreview.new_count} new` : ''}`}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ItemsPage;
